import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

//All Loading related components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import CommonLayout from "../../Common/Layout";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import DatasourceClass from "../../../service/dataSource";
import { navDatasets, navEntities, navDataExplorer, navQualityDashboard, LINK_OBJECT_TYPE } from "../../kmk/AppData";
import KMKNavigateButton from "../../Common/KMKNavigateButton";
import Statistics from "./Statistics";
import { ButtonUI, KMKBackButton } from "../../Common";

const useStyles = makeStyles()((theme) => ({
  IconHolder: {
    height: "40px",
    width: "40px",
    borderRadius: "80px",
    marginRight: "8px",
  },
  ifDisabled: {
    "& .Mui-disabled": {
      cursor: "not-allowed",
    },
  },
}));

const Index = () => {
  const { Workspace_id, type } = useParams();

  const { classes } = useStyles();
  const navigate = useNavigate();

  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, IsDataVaultEnabled } = workspace.workspace;

  const location = useLocation();
  const preSelectedId = useMemo(() => location?.state?.Object_id, [location?.state?.Object_id]);
  const fromDashboard = useMemo(() => location?.state?.fromDashboard, [location?.state?.fromDashboard]);

  const [loading, setLoading] = useState(false);
  const [disableDropdown, setDisableDropdown] = useState(false);
  const [objectList, setObjectList] = useState([]);
  const [selectedObject, setSelectedObject] = useState("");

  const navigateToHome = useCallback(
    () =>
      fromDashboard ? navigate(`/my-workspaces/${Workspace_id}`) : navigate(`/my-workspaces/${Workspace_id}/${type}`),
    [Workspace_id, fromDashboard, navigate, type]
  );

  const handleEditClick = useCallback(
    (id) => {
      navigate(
        `/my-workspaces/${Workspace_id}/${
          LINK_OBJECT_TYPE?.[type]?.objectType === 1 ? navDatasets.link : navEntities.link
        }/${id}`
      );
    },
    [Workspace_id, navigate, type]
  );

  useEffect(() => {
    if (IsDataVaultEnabled !== 1) return navigateToHome();
  }, [Workspace_id, IsDataVaultEnabled, navigate, navigateToHome]);

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      const datasource = new DatasourceClass();
      const result =
        LINK_OBJECT_TYPE?.[type]?.objectType === 1
          ? await datasource.getDatasourcesOnWorkspace(Workspace_id)
          : await datasource.getEntitiesOnWorkspace(Workspace_id);

      if (result.success) {
        setObjectList(result.data);

        const selectedObj = result.data?.find(({ id }) => id === preSelectedId);
        if (selectedObj) setSelectedObject(selectedObj);
      }

      setLoading(false);
    };

    getData();
  }, [Workspace_id, navigate, preSelectedId, type]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
      {
        link: `/my-workspaces/${Workspace_id}/${type}`,
        displayName: LINK_OBJECT_TYPE?.[type]?.plural || "",
        active: false,
      },
      { link: "#", displayName: "Statistics", active: true },
    ],
    [Workspace_id, type, workspaceName]
  );

  return (
    <CommonLayout
      title={
        <>
          <span>Statistics</span>
          <HelpContextIcon
            screen={LINK_OBJECT_TYPE?.[type]?.objectType === 1 ? "Dataset Statistics" : "Entity Statistics"}
          />
        </>
      }
      breadcrumbArr={breadcrumbArr}
    >
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          pb: 5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "12px 30px 30px 30px",
          }}
        >
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <KMKBackButton handleClick={navigateToHome}></KMKBackButton>
                <FormControl sx={{ width: { xs: "100%", sm: "300px" } }} variant={"standard"} required>
                  <InputLabel>Select {LINK_OBJECT_TYPE?.[type]?.singular || ""}</InputLabel>
                  <Select
                    className={classes.ifDisabled}
                    value={selectedObject.name || ""}
                    label={LINK_OBJECT_TYPE?.[type]?.singular || ""}
                    disabled={objectList.length === 0 || loading || disableDropdown}
                    onChange={(event) => setSelectedObject(objectList.find(({ name }) => name === event.target.value))}
                  >
                    {objectList.map((source) => (
                      <MenuItem key={source.name} value={source.name}>
                        {source.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedObject && type === "data-sources" && (
                  <ButtonUI
                    variant="contained"
                    style={{ minWidth: "80px", marginLeft: 15, background: "#0AB2B2" }}
                    onClick={() =>
                      navigate(`/my-workspaces/${Workspace_id}/${navQualityDashboard.link}`, {
                        state: { Name: encodeURIComponent(selectedObject.name) },
                      })
                    }
                  >
                    View in Data Quality Dashboard
                  </ButtonUI>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Edit Configuration">
                  <IconButton
                    className={classes.IconHolder}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(selectedObject.id);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditOutlinedIcon fontSize="inherit" color="primary" />
                  </IconButton>
                </Tooltip>
                {selectedObject?.isApplied && IsDataVaultEnabled !== 0 ? (
                  <KMKNavigateButton
                    iconButton
                    className={classes.IconHolder}
                    tooltipProps={{ title: "View in SQL Explorer", arrow: true }}
                    navigationProps={{
                      to: {
                        pathname: `/my-workspaces/${Workspace_id}/${navDataExplorer.link}/`,
                        search: `sidebarView=${encodeURIComponent("DataSet")}&objectName=${encodeURIComponent(
                          selectedObject.name
                        )}&schema=${encodeURIComponent("MODEL")}&expandWS=true`,
                      },
                    }}
                  >
                    <TableChartOutlinedIcon color="secondary" />
                  </KMKNavigateButton>
                ) : (
                  <></>
                )}
              </Box>
            </Grid>

            <Grid sx={{ width: "100%", display: "flex", alignItems: "center", marginBottom: "12px" }}>
              <Statistics
                Workspace_id={Workspace_id}
                tableName={selectedObject.name?.toUpperCase() || ""}
                dropdownLoading={loading}
                setDisableDropdown={setDisableDropdown}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </CommonLayout>
  );
};

export default Index;
